"use client";;
import { Button } from '@/app/ui/forms/button';
import { Skeleton } from '@/app/ui/imported/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/ui/imported/tooltip';
import Hero from '@/app/ui/utils/Hero';
import ProjectsImageSwitcher from '@/app/ui/utils/ProjectsImageSwitcher';
import Spinner from '@/app/ui/utils/Spinner';
import { db_getAllProjects } from '@/data/projects';
import { ActionResponseType } from '@/lib/definitions';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { AiFillCaretLeft, AiFillCaretRight } from "react-icons/ai";
import SocialImage from '../partials/SocialImage';

interface ProjectsImageSwitcherContextValues {
    next: boolean,
    prev: boolean,
    onChange: (imageIndex: number) => void,
    animating: boolean
}
export const ProjectsImageSwitcherContext = createContext({
    next: false,
    prev: false,
    onChange: (imageIndex: number) => { }
} as ProjectsImageSwitcherContextValues)

export default function ProjectsSwitcherHero({ projects, children }: { projects: ActionResponseType<typeof db_getAllProjects>, children?: ReactNode }) {
    const [next, setNext] = useState(false); //toggle to go next
    const [prev, setPrev] = useState(false); //toggle to go back
    const [animating, setAnimating] = useState(false); //animating state - prevent next/back spamming 
    const [currentIndex, setCurrentIndex] = useState(0);
    //create context controller allowing interaction with inner <ProjectsImageSwitcher/>
    const controller = {
        next,
        prev,
        onChange: setCurrentIndex,
    } as ProjectsImageSwitcherContextValues;

    //when index fully changed - stop animating state
    useEffect(() => {
        setAnimating(false)
    }, [currentIndex])

    const currentProject = projects[currentIndex];

    return <ProjectsImageSwitcherContext.Provider value={ controller }>
        <Hero bg={ <ProjectsImageSwitcher images={ projects.map(p => p.fileName) } animationDuration={ 1000 } duration={ 10000 } /> }>
            { children }
            { projects.length >= 1 &&
                <section className={ cn("absolute inset-0 top-auto bottom-4 mx-auto z-10 w-min pointer-events-auto",
                    "flex gxp-y-1 gap-x-4 justify-center flex-col sm:flex-row items-stretch sm:items-center",
                    "bg-slate-900 border border-nfPurple-500 shadow-lg shadow-black/50 p-2 rounded-xl text-center",
                    "h-fit w-11/12 sm:w-fit"
                ) }>

                    { projects.length > 1 && <NavigationBtn { ...{ animating, setPrev, prev, next, setAnimating, setNext } } /> }

                    <article className='flex flex-col'>
                        <h3 role="heading" className='text-lg leading-3 text-start'>פרוייקט:</h3>
                        <div className='text-2xl max-w-md line-clamp-3 sm:line-clamp-2 text-right relative min-w-44'>
                            { animating &&
                                <Skeleton className="absolute inset-x0 h-1em mb-2">.</Skeleton> ||
                                <p>{ currentProject.buildTitle }</p>
                            }
                        </div>
                    </article>

                    <div className='flex m-auto gap-2 flex-wrap justify-center pe-2 max-w-96 overflow-y-auto overflow-x-hidden min-h-10 max-h-24 snap-y snap-mandatory scroll-px-2 '>
                        {
                            animating &&
                            [0, 0, 0].map((_, i) => <Skeleton key={ i } className='rounded-full aspect-square w-9'>.</Skeleton>) ||
                            currentProject.users.map(user => {
                                if (!user.youtuber)
                                    return <SocialImage key={ user.discordID } src={ user.image } text={ user.name } size={ 35 } />

                                const { youtuber } = user;

                                return <Link key={ youtuber.ytid }
                                    className='snap-center snap-always'
                                    href={ `/creator/${youtuber.ytid}/${currentProject.seasonShortName}` }>
                                    <SocialImage src={ youtuber.image } text={ youtuber.name } size={ 35 } />
                                </Link>
                            })
                        }
                    </div>
                </section > }
        </Hero>
    </ProjectsImageSwitcherContext.Provider>
}

function NavigationBtn({ animating, setPrev, prev, next, setAnimating, setNext }:
    {
        animating: boolean, prev: boolean, next: boolean,
        setAnimating: Dispatch<SetStateAction<boolean>>, setPrev: Dispatch<SetStateAction<boolean>>, setNext: Dispatch<SetStateAction<boolean>>
    }) {
    return <article className="flex gap-2 justify-between order-1 sm:order-first">
        <Tooltip>
            <TooltipTrigger asChild>
                <Button colorScheme={ "blue" }
                    className="p-0 rounded-full aspect-square "
                    onClick={ () => { setPrev(!prev); setAnimating(true) } }
                    disabled={ animating }>
                    { animating && <Spinner /> || <AiFillCaretRight size={ "1.3em" } /> }
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                לפרוייקט הקודם
            </TooltipContent>
        </Tooltip>
        <Tooltip>
            <TooltipTrigger asChild>
                <Button colorScheme={ "blue" }
                    className="p-0 rounded-full aspect-square "
                    onClick={ () => { setNext(!next); setAnimating(true) } }
                    disabled={ animating }>
                    { animating && <Spinner /> || <AiFillCaretLeft size={ "1.3em" } /> }
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                לפרוייקט הבא
            </TooltipContent>
        </Tooltip>
    </article>

}